type BlockedByTask = {
  blockedBy?: string[] | null;
  blockedByPostApproval?: boolean | null;
};

type BlockerTask = {
  id: string;
  completedAt?: Date | string | null;
};

/**
 * Sibling tasks listed in `task.blockedBy` that are still open. A blocker that
 * no longer exists (deleted) is ignored rather than blocking forever.
 */
export function getIncompleteBlockers<T extends BlockerTask>(
  task: Pick<BlockedByTask, 'blockedBy'>,
  siblings: T[]
): T[] {
  return (task.blockedBy ?? [])
    .map((id) => siblings.find((t) => t.id === id))
    .filter((t): t is T => t !== undefined && !t.completedAt);
}

/** True while the task waits for its post to be approved (client approval gate). */
export function isAwaitingPostApproval(
  task: Pick<BlockedByTask, 'blockedByPostApproval'>,
  postApproved: boolean
): boolean {
  return !!task.blockedByPostApproval && !postApproved;
}

/**
 * A task is blocked while any task blocker is incomplete, or while it is gated
 * on post approval and the post is not approved yet.
 */
export function isTaskBlocked(
  task: BlockedByTask,
  siblings: BlockerTask[],
  postApproved: boolean
): boolean {
  return (
    getIncompleteBlockers(task, siblings).length > 0 || isAwaitingPostApproval(task, postApproved)
  );
}
